<template>
  <div class="vg-oneclick-pay">
    <div
      class="alert alert-primary vg-oneclick-pay-info"
      role="status"
      aria-live="polite"
    >
      <p class="m-0">{{ $t("messages.oneClickSavedPaymentInfo") }}</p>
    </div>
    <button
      type="button"
      class="vg-oneclick-pay-btn"
      :class="{ 'vg-oneclick-pay-btn--loading': isLoading }"
      :disabled="isLoading"
      :aria-busy="isLoading ? 'true' : 'false'"
      @click.prevent="oneClickAction"
    >
      <span
        v-if="isLoading"
        class="vg-oneclick-pay-btn__spinner"
        aria-hidden="true"
      ></span>
      <svg
        v-else
        class="vg-oneclick-pay-btn__icon"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm-3 8V6a3 3 0 1 1 6 0v3H9zm3 4a1.5 1.5 0 0 1 0 3 1.5 1.5 0 0 1 0-3z"
        />
      </svg>
      <span>{{ payButtonLabel }}</span>
    </button>
  </div>
</template>

<script>
import { useDraftStore } from "@/store/draft.js";
import { useCartStore } from "@/store/cart";
import { useAspspStore } from "@/store/aspsp";
import CartService from "@/services/cart";
import Orion from "@/services/orion";
import Redirect from "@/services/redirect";
import DuplicateOrderGuard from "@/services/duplicateOrderGuard";
import OneClickService from "@/services/payment/oneClick";
import { useOrderHistoryStore } from "@/store/orderHistory";
import Price from "@/services/price";
import { constants } from "@/constants";
import { stampConfirmationOfferWindow } from "@/services/confirmationOfferWindow";

export default {
  name: "CheckoutOneClick",
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    cartTotal() {
      return useCartStore().cart?.total;
    },
    payButtonLabel() {
      const amount =
        typeof this.cartTotal === "number" && this.cartTotal > 0
          ? `${Price.format(this.cartTotal)} €`
          : "";
      return this.$t("labels.payOrder", { amount });
    },
  },
  methods: {
    requestContactFormValidation() {
      // Meme chemin que le bouton Palyance dans l'iframe : touche les
      // champs, affiche les erreurs inline et scroll vers le premier bloc.
      window.dispatchEvent(
        new MessageEvent("message", {
          data: { contactFormCheckValidation: true },
        }),
      );
    },
    isReadyToSubmit() {
      const draftStore = useDraftStore();
      const cart = useCartStore().cart;
      const formConfig = window.contactFormConfig || {};

      if (!cart?.items?.length || !(cart.total > 0)) {
        return false;
      }

      if (formConfig.acceptCgv && !draftStore.areCgvApproved) {
        return false;
      }

      if (formConfig.acceptCgs && !draftStore.areCgsApproved) {
        return false;
      }

      if (!draftStore.isFormValid || !draftStore.isDraftOrderSent) {
        return false;
      }

      return true;
    },
    handleAlreadyClicked() {
      console.log("[OneClick] handleAlreadyClicked: start");

      const cartItem = useCartStore().cart?.items?.[0];
      if (cartItem) {
        console.log(
          "[OneClick] handleAlreadyClicked: removing cart item",
          cartItem,
        );
        CartService.deleteItem(cartItem);
      } else {
        console.log("[OneClick] handleAlreadyClicked: no cart item");
      }

      DuplicateOrderGuard.handleDuplicateBlocked(useCartStore().cart, {
        reason: "already_clicked",
      });
      console.log(
        "[OneClick] handleAlreadyClicked: redirecting to already-subscribed flow",
      );
    },
    async oneClickAction() {
      if (this.isLoading) {
        return;
      }

      if (!this.isReadyToSubmit()) {
        this.requestContactFormValidation();
        return;
      }

      this.isLoading = true;
      try {
        const alreadyClicked = await Orion.is1ClickLinkAlreadyUsed();
        if (!alreadyClicked.status) {
          const currentOrder =
            useOrderHistoryStore().lastOrderHistoryItem?.order;
          if (!currentOrder) {
            this.requestContactFormValidation();
            return;
          }

          const draftStore = useDraftStore();
          if (OneClickService.isEligibleToOneClick()) {
            const paymentType = useAspspStore().isAspspEligibleSepa
              ? constants.PAYMENT_METHOD_SEPA
              : constants.PAYMENT_METHOD_BANK_CARD;
            draftStore.selectPaymentMethod(paymentType);
          }

          const data = Orion.prepareOrder(
            useCartStore().cart,
            currentOrder,
            { orderType: "initial" },
            true,
          );

          const minifiedCart = CartService.minifyCart();

          await Orion.sendOrder(
            minifiedCart,
            data,
            window.globalConfig.domainId,
            "createOneClick",
          );

          data.orderMethod = "oneclick";
          useOrderHistoryStore().addOrderToHistory(
            useCartStore().cart,
            data,
            0,
          );

          const paramPlusData = stampConfirmationOfferWindow({
            ...(Redirect.getParamPlusData() || {}),
            orderId: data.externalOrderId,
            paymentType: data.paymentType,
          });
          localStorage.setItem(
            constants.PARAM_PLUS,
            JSON.stringify(paramPlusData),
          );
          Redirect.redirectToConfirmation(paramPlusData);
        } else {
          this.handleAlreadyClicked();
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
